import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Briefcase,
  CheckCircle2,
  ShieldCheck,
  Clock,
  Users,
  FileBarChart2,
  Lock,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_COPY = {
  insights: {
    headline: 'Visitor Insights',
    description:
      'Monthly visitor-attention reports — how visitors move through your venue, where they linger, and where to place revenue-generating placements.',
  },
  performance: {
    headline: 'Performance',
    description:
      'Forensic operational intelligence + benchmarks. Multiple recipients across your retail, programming, and visitor-services teams.',
  },
  enterprise: {
    headline: 'Enterprise Intelligence',
    description:
      'Full data partnership. Custom dashboards, on-call analyst, board-ready quarterly briefings, white-label PDFs.',
  },
};

const AnalyticsOfferAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/analytics-offer/by-token/${token}`);
        setOffer(data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Offer not found or link expired');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const accept = async () => {
    if (!agreed) {
      toast.error('Please accept the agreement');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/analytics-offer/${token}/accept`, {
        agreement_accepted: true,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-rose-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Invitation unavailable</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/for-partners')}
            className="px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
            data-testid="go-to-partners-btn"
          >
            Go to partners page
          </button>
        </div>
      </div>
    );
  }

  const status = offer.status;
  const finalPrice = Number(offer.final_price_usd || 0);
  const interval = offer.billing_interval === 'yearly' ? 'year' : 'month';
  const tierCopy = TIER_COPY[offer.tier] || TIER_COPY.insights;
  const alreadyActive = status === 'active';
  const expiredOrCanceled = status === 'expired' || status === 'canceled';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-fuchsia-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden" data-testid="offer-acceptance-page">
          <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white p-8">
            <div className="flex items-center gap-3 mb-3">
              <Briefcase className="w-7 h-7" />
              <span className="text-sm uppercase font-bold tracking-wider text-violet-100">
                Audiostride Visitor Intelligence
              </span>
            </div>
            <h1 className="text-3xl font-black mb-2">Hello {offer.contact_name},</h1>
            <p className="text-violet-100 text-lg">
              Your private subscription proposal for <strong>{offer.org_name}</strong> is ready.
            </p>
          </div>

          <div className="p-8 space-y-8">
            {alreadyActive && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3" data-testid="already-active-banner">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-bold text-emerald-900">Subscription is active.</p>
                  <p className="text-sm text-emerald-800">Reports will arrive at the cadence below. We'll email you confirmations and renewal notices.</p>
                </div>
              </div>
            )}

            {expiredOrCanceled && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="offer-closed-banner">
                <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-bold text-amber-900">This offer is no longer active.</p>
                  <p className="text-sm text-amber-800">Please contact the Audiostride team for a refreshed proposal.</p>
                </div>
              </div>
            )}

            <div>
              <h2 className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Tier</h2>
              <h3 className="text-2xl font-black text-slate-900">{tierCopy.headline}</h3>
              <p className="text-slate-600 mt-2">{tierCopy.description}</p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-black text-slate-900">${finalPrice.toFixed(0)}</span>
                <span className="text-slate-600 font-medium">/ {interval}</span>
                {offer.discount_pct > 0 && (
                  <span className="ml-3 px-2 py-1 rounded bg-emerald-100 text-emerald-800 text-xs font-bold">
                    {offer.discount_pct}% off
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Billed {offer.billing_interval} · auto-renewing · cancel anytime
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Bullet icon={FileBarChart2} title="Tour covered" detail={offer.tour_title} />
              <Bullet icon={Clock} title="Report cadence" detail={cap(offer.report_cadence)} />
              <Bullet icon={Users} title="Recipients" detail={`Up to ${offer.max_recipients} on your team`} />
              <Bullet
                icon={ShieldCheck}
                title="Branding"
                detail={offer.white_label ? 'White-label (your brand only)' : offer.co_branded ? 'Co-branded' : 'Audiostride branded'}
              />
            </div>

            <div className="bg-violet-50 border border-violet-200 rounded-xl p-5 space-y-2 text-sm text-slate-700">
              <p className="font-bold text-violet-900">What you receive in every report</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Visitor attention map for your physical space (where visitors linger, where they leave)</li>
                <li>Forensic diagnoses with specific stop-area observations and quantified projected lift</li>
                <li>Prioritized partner-side actions for retail, programming, signage, sponsorships, membership</li>
                <li>Economic value of visitor attention this period (engagement + media-equivalent + loyalty value)</li>
                <li>Forecasts &amp; period-over-period trends for your planning cycle</li>
              </ul>
            </div>

            {!alreadyActive && !expiredOrCanceled && (
              <>
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-black text-slate-900 mb-2 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-slate-700" /> Subscription agreement
                  </h3>
                  <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-4 max-h-40 overflow-y-auto leading-relaxed space-y-2">
                    <p>
                      By accepting, <strong>{offer.org_name}</strong> ("Subscriber") subscribes to Audiostride
                      Visitor Intelligence reports for the tour <em>{offer.tour_title}</em> at{' '}
                      <strong>${finalPrice.toFixed(0)}/{interval}</strong>, billed via Stripe and auto-renewing.
                    </p>
                    <p>
                      <strong>Deliverables.</strong> Audiostride will deliver a {offer.report_cadence} PDF
                      report to up to {offer.max_recipients} named recipients. Reports are observational
                      analyses of visitor behavior in Subscriber's physical space and contain no personally
                      identifiable visitor data.
                    </p>
                    <p>
                      <strong>Confidentiality &amp; usage.</strong> Reports are licensed to Subscriber for
                      internal use including board materials, sponsor decks, and operational planning.
                      Reports may not be resold to third parties.
                    </p>
                    <p>
                      <strong>Cancellation.</strong> Subscriber may cancel at any time; access continues
                      through the end of the paid period. Audiostride may pause delivery for non-payment
                      after a 7-day grace period.
                    </p>
                    <p>
                      <strong>Data.</strong> All visitor analytics derive from anonymized Audiostride tour
                      sessions. Audiostride retains ownership of underlying data; Subscriber receives a
                      perpetual license to the report contents.
                    </p>
                    <p>
                      <strong>Liability.</strong> Reports are decision-support analyses, not guarantees.
                      Audiostride's total liability is capped at fees paid in the 12 months prior to a claim.
                    </p>
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 w-5 h-5"
                    data-testid="agreement-checkbox"
                  />
                  <span className="text-sm text-slate-700">
                    I have read and accept the agreement on behalf of {offer.org_name}, and I'm authorized
                    to commit the organization to a recurring subscription.
                  </span>
                </label>

                <button
                  onClick={accept}
                  disabled={!agreed || submitting}
                  data-testid="accept-and-pay-btn"
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-90 text-white py-4 rounded-full font-bold text-lg shadow-lg transition disabled:opacity-50"
                >
                  {submitting ? 'Redirecting to Stripe…' : `Accept & pay $${finalPrice.toFixed(0)}/${interval}`}
                </button>

                <p className="text-xs text-slate-500 text-center">
                  Payment is processed securely by Stripe. You will be redirected to complete checkout.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Bullet = ({ icon: Icon, title, detail }) => (
  <div className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-4">
    <Icon className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" />
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{title}</div>
      <div className="font-bold text-slate-900">{detail}</div>
    </div>
  </div>
);

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

export default AnalyticsOfferAcceptPage;
